import Link from 'next/link'
import { ComponentProps, type ForwardedRef, forwardRef } from 'react'
import { Link as NextIntlLink } from '@/i18n/navigation'
import { TextLink as TextLinkComponent, headlessLinkProps } from '@betterplace/design-system/server'
import { urlToString } from '@/helpers/utils/routing/Routes'
type TextLinkProps = Omit<ComponentProps<typeof TextLinkComponent>, 'href'> & Pick<ComponentProps<typeof Link>, 'href'>
type LocaleAwareTextLinkProps = Omit<ComponentProps<typeof TextLinkComponent>, 'href'> &
  Pick<ComponentProps<typeof NextIntlLink>, 'href' | 'locale' | 'lang'>
const TextLink = forwardRef(function TextLink(
  { href, external = false, ...props }: TextLinkProps & { external?: boolean },
  ref: ForwardedRef<HTMLAnchorElement>
) {
  if (external) return <ExternalLink ref={ref} href={href} {...props} />
  return <InternalLink ref={ref} href={href} {...props} />
})

const LocaleAwareLink = forwardRef(function LocaleAwareLink(
  { href, locale, lang, ...props }: LocaleAwareTextLinkProps & { external?: boolean },
  ref: ForwardedRef<HTMLAnchorElement>
) {
  const { children, as: _, ...props_ } = headlessLinkProps(props)

  return (
    <NextIntlLink ref={ref} href={href} locale={locale} lang={lang} {...props_}>
      {children}
    </NextIntlLink>
  )
})

const InternalLink = forwardRef(function InternalTextLink(
  { href, ...props }: TextLinkProps,
  ref: ForwardedRef<HTMLAnchorElement>
) {
  const { children, as: _, ...props_ } = headlessLinkProps(props)

  return (
    <Link ref={ref} href={href} {...props_}>
      {children}
    </Link>
  )
})

const ExternalLink = forwardRef(function ExternalTextLink(
  { href, as: _, ...props }: TextLinkProps,
  ref: ForwardedRef<HTMLAnchorElement>
) {
  const href_ = urlToString(href)
  return <TextLinkComponent ref={ref} href={href_} {...props} />
})

export default TextLink
export { LocaleAwareLink }
